import * as React from "react";
import { Modal, Table, Input, Button, Tag } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { getCouponListApi, type CouponListItem, type CouponType } from "src/api/coupon/couponApi";
import { snackbarStore } from "src/store/snackbarStore";

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (coupon: CouponListItem) => void;
};

const toast = (message: string, severity: "success" | "error" | "info" | "warning" = "info") =>
  snackbarStore.getState().addSnackbar({ message, severity });

const getCouponTypeLabel = (type: CouponType) => {
  const labels: Record<CouponType, string> = {
    PUBLIC: "공개",
    PRIVATE: "비공개",
    AUTO: "자동",
    EVENT: "이벤트",
    FREE_SHIPPING: "무료배송",
  };
  return labels[type];
};

export default function CouponSelectModal({ open, onClose, onSelect }: Props) {
  const [loading, setLoading] = React.useState(false);
  const [coupons, setCoupons] = React.useState<CouponListItem[]>([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const [searchText, setSearchText] = React.useState("");

  const fetchCoupons = React.useCallback(async () => {
    try {
      setLoading(true);
      const res = await getCouponListApi({
        page,
        size: pageSize,
      });

      if (res.statusCode === 1 && res.data) {
        setCoupons(res.data.content);
        setTotal(res.data.page.totalElements);
      } else {
        toast(res.message || "쿠폰 목록을 불러올 수 없습니다.", "error");
      }
    } catch (e: any) {
      toast(e?.message || "쿠폰 목록을 불러오는 중 오류가 발생했습니다.", "error");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  React.useEffect(() => {
    if (open) {
      fetchCoupons();
    } else {
      setSearchText("");
      setPage(0);
    }
  }, [open, fetchCoupons]);

  const filteredCoupons = React.useMemo(() => {
    if (!searchText) return coupons;
    const lowerSearch = searchText.toLowerCase();
    return coupons.filter(
      (coupon) =>
        coupon.couponName.toLowerCase().includes(lowerSearch) ||
        coupon.couponCode.toLowerCase().includes(lowerSearch) ||
        coupon.couponId.toString().includes(lowerSearch)
    );
  }, [coupons, searchText]);

  const handleSelect = (coupon: CouponListItem) => {
    onSelect(coupon);
    onClose();
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "couponId",
      width: 80,
    },
    {
      title: "쿠폰명",
      dataIndex: "couponName",
      width: 200,
    },
    {
      title: "쿠폰 코드",
      dataIndex: "couponCode",
      width: 150,
    },
    {
      title: "타입",
      dataIndex: "couponType",
      width: 100,
      render: (type: CouponType) => <Tag>{getCouponTypeLabel(type)}</Tag>,
    },
    {
      title: "할인",
      dataIndex: "discountType",
      width: 120,
      render: (_: any, record: CouponListItem) => {
        if (record.discountType === "PERCENTAGE") {
          return `${record.discountValue}%`;
        }
        return `${record.discountValue.toLocaleString()}원`;
      },
    },
    {
      title: "상태",
      dataIndex: "isActive",
      width: 80,
      render: (isActive: boolean) => (
        <span style={{ color: isActive ? "#52c41a" : "#999" }}>{isActive ? "활성" : "비활성"}</span>
      ),
    },
    {
      title: "액션",
      width: 100,
      align: "center" as const,
      fixed: "right" as const,
      render: (_: any, record: CouponListItem) => (
        <Button type="primary" size="small" onClick={() => handleSelect(record)}>
          선택
        </Button>
      ),
    },
  ];

  return (
    <Modal
      title="쿠폰 선택"
      open={open}
      onCancel={onClose}
      width={1000}
      footer={[
        <Button key="cancel" onClick={onClose}>
          취소
        </Button>,
      ]}
      styles={{
        body: {
          maxHeight: "70vh",
          overflowY: "auto",
        },
      }}
    >
      <div style={{ marginBottom: 16 }}>
        <Input
          placeholder="쿠폰 ID, 이름, 코드로 검색"
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
      </div>
      <Table
        columns={columns}
        dataSource={filteredCoupons}
        rowKey="couponId"
        loading={loading}
        pagination={{
          current: page + 1,
          pageSize,
          total,
          onChange: (p, ps) => {
            setPage(p - 1);
            setPageSize(ps);
          },
          showSizeChanger: true,
          showTotal: (total) => `총 ${total}개`,
        }}
        size="small"
        scroll={{ x: 1000 }}
      />
    </Modal>
  );
}
